import { Object3D, Vector3 } from 'three';
import { ProjectRenderer } from '../ProjectRenderer';
import { MeasurementOffsetAxis } from '../types';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import RENDERER_CONFIG from '@/configs/rendererConfig';

const lineMaterial = new LineMaterial({
	color: 0x000000,
	linewidth: 1,
});

export class Measurement {
	private length = 0;
	private css2dObject!: CSS2DObject;
	private divElement!: HTMLDivElement;
	private labelElement!: HTMLDivElement;
	private material = lineMaterial.clone();
	private line = new Line2(new LineGeometry(), this.material);
	private extensionLines: [Line2, Line2] = [
		new Line2(new LineGeometry(), this.material),
		new Line2(new LineGeometry(), this.material),
	];
	private _visible = false;
	id: string;
	label: string;
	offsetAxis: MeasurementOffsetAxis;
	/** Meters, signed along offsetAxis */
	offsetLength: number;
	labelOffset: Vector3;
	/** Degrees, counter-clockwise, 0 = horizontal */
	labelRotation: number;
	points: [Object3D, Object3D];

	constructor(
		protected renderer: ProjectRenderer,
		data: Pick<
			Measurement,
			'id' | 'label' | 'offsetAxis' | 'labelOffset' | 'points'
		> &
			Partial<Pick<Measurement, 'offsetLength' | 'labelRotation'>>
	) {
		this.id = data.id;
		this.label = data.label;
		this.offsetAxis = data.offsetAxis;
		this.offsetLength =
			data.offsetLength ?? RENDERER_CONFIG.MEASUREMENT.OFFSET;
		this.labelOffset = data.labelOffset;
		this.labelRotation = data.labelRotation ?? 0;
		this.points = data.points;

		this.initLabel();
		this.initLine();
		this.compute();

		this.visible = false;
	}

	get visible() {
		return this._visible;
	}

	set visible(value: boolean) {
		this._visible = value;
		this.css2dObject.visible = value;
		this.line.visible = value;

		for (const extensionLine of this.extensionLines) {
			extensionLine.visible = value;
		}
	}

	private initLabel() {
		this.divElement = document.createElement('div');
		this.labelElement = document.createElement('div');
		this.labelElement.className = 'measure-label';

		if (this.labelRotation !== 0)
			this.labelElement.style.transform = `rotate(${-this.labelRotation}deg)`;

		this.divElement.appendChild(this.labelElement);

		this.css2dObject = new CSS2DObject(this.divElement);
		this.css2dObject.layers.set(RENDERER_CONFIG.MEASUREMENT.LAYER);

		this.renderer.scene.add(this.css2dObject);
	}

	private initLine() {
		for (const line of [this.line, ...this.extensionLines]) {
			line.layers.set(RENDERER_CONFIG.MEASUREMENT.LAYER);
			this.renderer.scene.add(line);
		}
	}

	private getOffset() {
		switch (this.offsetAxis) {
			case MeasurementOffsetAxis.X:
				return new Vector3(this.offsetLength, 0, 0);
			case MeasurementOffsetAxis.Y:
				return new Vector3(0, this.offsetLength, 0);
			case MeasurementOffsetAxis.Z:
				return new Vector3(0, 0, this.offsetLength);
			default:
				return new Vector3();
		}
	}

	protected getLabelValue() {
		return Math.round(this.length * 1000);
	}

	private compute() {
		const [start, end] = this.points;
		const offset = this.getOffset();

		const startWorldPosition = start.getWorldPosition(new Vector3());
		const endWorldPosition = end.getWorldPosition(new Vector3());

		this.length = startWorldPosition.distanceTo(endWorldPosition);

		const lineStart = startWorldPosition.clone().add(offset);
		const lineEnd = endWorldPosition.clone().add(offset);
		const center = lineStart.clone().lerp(lineEnd, 0.5);

		this.labelElement.innerText = `${this.getLabelValue()}`;

		this.css2dObject.position.copy(center).add(this.labelOffset);
		this.line.geometry.setPositions([
			...lineStart.toArray(),
			...lineEnd.toArray(),
		]);

		const [startExtensionLine, endExtensionLine] = this.extensionLines;

		startExtensionLine.geometry.setPositions([
			...startWorldPosition.toArray(),
			...lineStart.toArray(),
		]);
		endExtensionLine.geometry.setPositions([
			...endWorldPosition.toArray(),
			...lineEnd.toArray(),
		]);
	}

	draw() {
		this.compute();
	}

	dispose() {
		this.css2dObject.removeFromParent();

		for (const line of [this.line, ...this.extensionLines]) {
			line.removeFromParent();
			line.geometry.dispose();
		}

		this.material.dispose();
	}
}
